import { useEffect } from 'react';
import { productPath } from './routes';

export const SITE_URL = 'https://aavjoind.com';
export const SITE_NAME = 'Aavjo Industries';

export const HOME_SEO = {
  title: 'Aerial Lift Manufacturer in Ahmedabad | Aavjo Industries',
  description:
    'Aavjo Industries manufactures aerial lifts, scissor lifts, goods lifts, stackers and pallet trucks in Ahmedabad, Gujarat. ISO 9001 hydraulic lifting equipment for warehouses, factories and construction sites across India.',
  keywords:
    'aerial lift, aerial lift manufacturer, aerial work platform, scissor lift, goods lift, hydraulic lift, stacker, pallet truck, material handling equipment, aerial lift Ahmedabad, aerial lift India, Aavjo Industries',
  path: '/',
};

const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': ['Organization', 'LocalBusiness'],
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/aavjo-icon.jpg`,
  image: `${SITE_URL}/aavjo-icon.jpg`,
  telephone: '+91-9328058417',
  email: 'aavjoindustries@gmail.com',
  description:
    'Manufacturer of aerial lifts, scissor lifts, goods lifts, stackers, pallet trucks and material handling equipment in Ahmedabad, Gujarat, India.',
  address: {
    '@type': 'PostalAddress',
    streetAddress:
      '42, Syamved Industrial Estate, SP Ring Road, Ranasan, Opposite Gamthi Hotel, GIDC Naroda',
    addressLocality: 'Ahmedabad',
    postalCode: '382340',
    addressRegion: 'Gujarat',
    addressCountry: 'IN',
  },
  areaServed: 'IN',
  sameAs: [
    'https://facebook.com/share/1FJZ7yDuuS',
    'https://instagram.com/aavjoindustries',
  ],
});

export const productsItemListSchema = (products) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Aavjo Industries Aerial Lifts and Material Handling Equipment',
  itemListElement: products.map((product, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    url: `${SITE_URL}${encodeURI(productPath(product))}`,
    name: `${product.name} ${product.category}`,
  })),
});

export const productSchema = (product) => {
  const description =
    product.paragraphs?.[0] ||
    `${product.name} ${product.category} manufactured by Aavjo Industries in Ahmedabad, India.`;
  const image = product.image?.startsWith('http')
    ? product.image
    : `${SITE_URL}${product.image}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${product.name} ${product.category}`,
    description,
    image,
    sku: product.name,
    brand: { '@type': 'Brand', name: SITE_NAME },
    manufacturer: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    category: product.category,
    url: `${SITE_URL}${encodeURI(productPath(product))}`,
  };
};

export const useSeo = ({
  title,
  description,
  keywords,
  path,
  image,
  robots,
  jsonLd,
}) => {
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    const url = encodeURI(`${SITE_URL}${path || '/'}`);
    const ogImage = image || `${SITE_URL}/aavjo-icon.jpg`;

    document.title = title;
    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);
    setMeta('name', 'robots', robots || 'index, follow');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:image', ogImage);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setCanonical(url);
    setJsonLd('org-schema', organizationSchema());

    if (jsonLdString) {
      setJsonLd('page-schema', JSON.parse(jsonLdString));
    } else {
      document.getElementById('page-schema')?.remove();
    }
  }, [title, description, keywords, path, image, robots, jsonLdString]);
};
